import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Autocomplete, TextField, ToggleButtonGroup, ToggleButton,
    Chip, IconButton, Card, CardContent, Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BuildIcon from '@mui/icons-material/Build';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PlaceIcon from '@mui/icons-material/Place';
import { initializeDB } from '../../config/db';
import { JOB_STORE } from '../../config/constants';
import InternalEquipmentTable from './InternalEquipmentTable';
import RentalEquipmentTable from './RentalEquipmentTable';
import ConflictDialog from './ConflictDialog';
import poEditService from '../../services/poEditService';

const MODE_JOB = 'job';
const MODE_LOCATION = 'location';
const TAB_RENTAL = 'rental';
const TAB_INTERNAL = 'internal';

function buildLocationOptions(jobs) {
    const seen = new Map();
    jobs.forEach(job => {
        const rawLocation = job.location || job.Location || '';
        if (!rawLocation) return;
        const key = rawLocation.trim().toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, { label: rawLocation.trim(), jobCodes: [job.Job] });
        } else {
            const entry = seen.get(key);
            if (job.Job && !entry.jobCodes.includes(job.Job)) {
                entry.jobCodes.push(job.Job);
            }
        }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
}

const EquipmentList = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [filterMode, setFilterMode] = useState(MODE_JOB);
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [conflictData, setConflictData] = useState(null);
    const [activeTab, setActiveTab] = useState(TAB_RENTAL);

    useEffect(() => {
        const loadJobs = async () => {
            const db = await initializeDB();
            const allJobs = await db.getAll(JOB_STORE);
            setJobs(allJobs.filter(j => j.Job));
        };
        loadJobs();
    }, []);

    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    const locationOptions = useMemo(() => buildLocationOptions(jobs), [jobs]);

    const selectedJobCodes = useMemo(() => {
        if (filterMode === MODE_JOB) {
            return selectedJob ? [selectedJob.Job] : [];
        }
        return selectedLocation ? (selectedLocation.jobCodes || []) : [];
    }, [filterMode, selectedJob, selectedLocation]);

    useEffect(() => {
        const updatePendingCount = async () => {
            const pending = await poEditService.getPendingEdits();
            setPendingCount(pending.length);
        };
        updatePendingCount();
        const handlers = ['poEditSyncComplete', 'poEditSyncStart'].map(evt => {
            const handler = () => updatePendingCount();
            window.addEventListener(evt, handler);
            return { evt, handler };
        });
        return () => handlers.forEach(({ evt, handler }) => window.removeEventListener(evt, handler));
    }, []);

    useEffect(() => {
        const handler = (e) => setConflictData(e.detail);
        window.addEventListener('poEditConflict', handler);
        return () => window.removeEventListener('poEditConflict', handler);
    }, []);

    const handleModeChange = useCallback((_, newMode) => {
        if (!newMode) return;
        setFilterMode(newMode);
        setSelectedJob(null);
        setSelectedLocation(null);
    }, []);

    const handleConflictOverwrite = useCallback(async () => {
        if (conflictData?.edit) {
            await poEditService.forceSyncEdit(conflictData.edit);
            setConflictData(null);
        }
    }, [conflictData]);

    const handleConflictCancel = useCallback(async () => {
        if (conflictData?.edit) {
            await poEditService.cancelEdit(conflictData.edit.localId);
            setConflictData(null);
            window.dispatchEvent(new Event('poEditReverted'));
        }
    }, [conflictData]);

    const hasSelection = selectedJobCodes.length > 0;

    return (
        <Box sx={{ position: 'fixed', top: '60px', left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
            {/* Header */}
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 1.5,
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start" size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <BuildIcon sx={{ color: '#455a64', fontSize: 28 }} />
                        <Typography variant="h6" fontWeight={600}>Equipment List</Typography>
                    </Box>
                    {pendingCount > 0 && (
                        <Chip
                            label={`${pendingCount} pending`}
                            color="warning"
                            size="small"
                            sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 } }}>
                {/* Selector — toggle + input on one row */}
                <Card elevation={1} sx={{ mb: 1, flexShrink: 0 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                            <ToggleButtonGroup
                                color="primary"
                                value={filterMode}
                                exclusive
                                onChange={handleModeChange}
                                size="small"
                                sx={{ flexShrink: 0 }}
                            >
                                <ToggleButton value={MODE_JOB} sx={{ px: 2 }}>
                                    <WorkOutlineIcon sx={{ mr: 0.75 }} fontSize="small" /> Job
                                </ToggleButton>
                                <ToggleButton value={MODE_LOCATION} sx={{ px: 2 }}>
                                    <PlaceIcon sx={{ mr: 0.75 }} fontSize="small" /> Location
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {filterMode === MODE_JOB ? (
                                <Autocomplete
                                    value={selectedJob}
                                    onChange={(_, v) => setSelectedJob(v)}
                                    options={sortedJobs}
                                    getOptionLabel={(o) => {
                                        const code = o.Job || '';
                                        const name = o.JobName || '';
                                        return code && name ? `${code} \u2022 ${name}` : (code || name || '');
                                    }}
                                    isOptionEqualToValue={(o, v) => o.Job === v?.Job}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Job" placeholder="Search job..." size="small" />
                                    )}
                                    clearOnBlur={false}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                            ) : (
                                <Autocomplete
                                    value={selectedLocation}
                                    onChange={(_, v) => { setSelectedLocation(v); setSelectedJob(null); }}
                                    options={locationOptions}
                                    getOptionLabel={(o) => o.label || ''}
                                    isOptionEqualToValue={(o, v) =>
                                        o.label?.toLowerCase() === v?.label?.toLowerCase()
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Location" placeholder="Search location..." size="small" />
                                    )}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Table area */}
                {!hasSelection ? (
                    <Card elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                        <BuildIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                            {filterMode === MODE_LOCATION
                                ? 'Select a location to view equipment for all jobs at that site.'
                                : 'Select a job to view equipment.'}
                        </Typography>
                    </Card>
                ) : (
                    <Card elevation={1} sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            {/* Tab buttons */}
                            <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                                <Button
                                    onClick={() => setActiveTab(TAB_RENTAL)}
                                    sx={{
                                        flex: 1,
                                        py: 1.25,
                                        borderRadius: 0,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: activeTab === TAB_RENTAL ? '#1976d2' : 'text.secondary',
                                        borderBottom: activeTab === TAB_RENTAL ? '3px solid #1976d2' : '3px solid transparent',
                                        backgroundColor: activeTab === TAB_RENTAL ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                    }}
                                >
                                    Rental Equipment
                                </Button>
                                <Button
                                    onClick={() => setActiveTab(TAB_INTERNAL)}
                                    sx={{
                                        flex: 1,
                                        py: 1.25,
                                        borderRadius: 0,
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: activeTab === TAB_INTERNAL ? '#455a64' : 'text.secondary',
                                        borderBottom: activeTab === TAB_INTERNAL ? '3px solid #455a64' : '3px solid transparent',
                                        backgroundColor: activeTab === TAB_INTERNAL ? 'rgba(69, 90, 100, 0.04)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(69, 90, 100, 0.08)' },
                                    }}
                                >
                                    Internal Equipment
                                </Button>
                            </Box>

                            {/* Hint for rental */}
                            {activeTab === TAB_RENTAL && (
                                <Box sx={{ px: 2, py: 0.5, flexShrink: 0 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Tap highlighted cells to edit
                                    </Typography>
                                </Box>
                            )}

                            {/* Active table — full height */}
                            {activeTab === TAB_RENTAL ? (
                                <RentalEquipmentTable selectedJobCodes={selectedJobCodes} jobs={jobs} />
                            ) : (
                                <InternalEquipmentTable selectedJobCodes={selectedJobCodes} jobs={jobs} />
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>

            <ConflictDialog
                open={!!conflictData}
                conflictData={conflictData}
                onOverwrite={handleConflictOverwrite}
                onCancel={handleConflictCancel}
            />
        </Box>
    );
};

export default EquipmentList;
